
import * as React from 'react';
import './App.css';
import { ChecksTable } from './ChecksTable';
import { auth, googleProvider, signInWithPopup, signOut, onAuthStateChanged, getChecks, saveCheck } from './firebase';
import { toWords } from './toWords';
import { validateAmount } from './validations';

function App() {
  const [user, setUser] = React.useState(null)
  const [authLoading, setAuthLoading] = React.useState(true)
  const [notes, setNotes] = React.useState('');
  const [amount, setAmount] = React.useState('')
  const [payee, setPayee] = React.useState('')
  const [memo, setMemo] = React.useState('')
  const [checkNumber, setCheckNumber] = React.useState('')
  const [checks, setChecks] = React.useState([])
  const [checkData, setCheckData] = React.useState(null)

  const setStateFunctions = {
    notes: setNotes,
    amount: setAmount,
    payee: setPayee,
    memo: setMemo,
    checkNumber: setCheckNumber,
  }

  // Track auth state
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      setAuthLoading(false)
    })
    return unsubscribe
  }, [])

  // Load checks when user signs in (migrate unowned checks on first load)
  React.useEffect(() => {
    if (!user) return
    getChecks(user.uid).then(qs => {
      let newChecks = []
      qs.forEach((doc) => {
        newChecks.push(doc.data())
      });
      newChecks.sort((a, b) => (b.id || 0) - (a.id || 0))
      setChecks(newChecks)
    }).catch((error) => {
      console.log('Error loading checks:', error.message)
    })
  }, [user])

  function handleSignIn() {
    signInWithPopup(auth, googleProvider).catch((error) => {
      console.error('Sign in error:', error.message)
    })
  }

  function handleSignOut() {
    signOut(auth).then(() => {
      setChecks([])
      setCheckData(null)
    })
  }

  function escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = String(str)
    return div.innerHTML
  }

  const [generating, setGenerating] = React.useState(false)

  function generateCheck() {
    const formattedDate = new Date().toLocaleDateString()
    const formattedAmount = parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const textAmount = toWords(amount)

    const data = {
      date: formattedDate,
      amount: formattedAmount,
      textAmount: textAmount,
      payee: payee,
      memo: memo,
      notes: notes || '',
    }

    setCheckData(data)
    setGenerating(true)
    generatePdf(data).finally(() => setGenerating(false))

    saveCheck({
      amount: amount,
      payee: payee,
      memo: memo,
      date: new Date().toDateString(),
      notes: notes,
      checkNumber: checkNumber,
      id: new Date().getTime()
    }, user.uid)
  }

  function buildCheckHtml(data) {
    const safe = (str) => {
      const div = document.createElement('div')
      div.textContent = String(str)
      return div.innerHTML
    }

    const stubHtml = (title) =>
      '<div class="stub">' +
        '<div class="stub-title">' + title + '</div>' +
        '<div class="stub-row"><span class="lbl">Date:</span> ' + safe(data.date) + '</div>' +
        '<div class="stub-row"><span class="lbl">Amount:</span> ' + safe(data.amount) + '</div>' +
        '<div class="stub-row"><span class="lbl">Pay to:</span> ' + safe(data.payee) + '</div>' +
        '<div class="stub-row"><span class="lbl">Memo:</span> ' + safe(data.memo) + '</div>' +
        '<div class="stub-row"><span class="lbl">Notes:</span> ' + safe(data.notes) + '</div>' +
      '</div>'

    return '<!DOCTYPE html><html><head><meta charset="UTF-8"><style>' +
      '@page { size: letter; margin: 0; }' +
      '* { box-sizing: border-box; margin: 0; padding: 0; }' +
      'body { font-family: "Courier New", monospace; font-size: 12px; }' +
      '.page { width: 8.5in; height: 11in; }' +
      '.check-section { height: 3.67in; padding: 0.95in 0.5in 0in; }' +
      '.check-date { text-align: right; font-size: 14px; font-weight: bold; margin-top: -0.25in; margin-bottom: 0.5in; padding-bottom: 0.2in; }' +
      '.check-payee { font-size: 14px; font-weight: bold; padding-bottom: 0.15in; padding-left: 0.5in; position: relative; top: -0.25in; }' +
      '.check-amount-line { text-align: right; font-size: 14px; font-weight: bold; margin-top: -0.5in; margin-bottom: 0.5in; padding-bottom: 0.15in; position: relative; top: -0.125in; }' +
      '.check-words { font-size: 13px; font-weight: bold; padding-bottom: 0.15in; padding-left: 0.5in; position: relative; top: -0.25in; }' +
      '.check-memo { font-size: 13px; padding-top: 0.3in; }' +
      '.check-memo .lbl { font-size: 10px; }' +
      '.check-memo .val { font-weight: bold; }' +
      '.stub { height: 3.665in; padding: 0.3in 0.5in; }' +
      '.stub-title { font-weight: bold; font-size: 12px; text-align: center; padding-bottom: 0.2in; }' +
      '.stub-row { font-size: 12px; padding: 3px 0; }' +
      '.stub-row .lbl { font-weight: bold; display: inline-block; width: 70px; }' +
      '</style></head><body><div class="page">' +
        '<div class="check-section">' +
          '<div class="check-date">' + safe(data.date) + '</div>' +
          '<div class="check-payee">' + safe(data.payee) + '</div>' +
          '<div class="check-amount-line">' + safe(data.amount) + '</div>' +
          '<div class="check-words">' + safe(data.textAmount) + '</div>' +
          '<div class="check-memo"><span class="val">' + safe(data.memo) + '</span></div>' +
        '</div>' +
        stubHtml('RECORD STUB - RETAIN FOR YOUR RECORDS') +
        stubHtml('VOUCHER STUB - SEND WITH PAYMENT') +
      '</div></body></html>'
  }

  async function generatePdf(data) {
    const html = buildCheckHtml(data)

    try {
      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'text/html' },
        body: html,
      })

      if (!response.ok) {
        const err = await response.text()
        console.error('PDF generation failed:', err)
        alert('PDF generation failed. Check console for details.')
        return
      }

      const pdfBlob = await response.blob()
      const url = URL.createObjectURL(pdfBlob)
      window.open(url, '_blank')
    } catch (err) {
      console.error('PDF generation error:', err)
      alert('Could not generate PDF. Check console for details.')
    }
  }

  // Loading state
  if (authLoading) {
    return (
      <div className="app-container">
        <div className="card" style={{ textAlign: 'center', padding: '60px 32px' }}>
          <p style={{ color: '#666', fontSize: 16 }}>Loading...</p>
        </div>
      </div>
    )
  }

  // Sign-in screen
  if (!user) {
    return (
      <div className="app-container">
        <div className="card login-card">
          <h1 className="card-title">Business Check Printer</h1>
          <p className="login-subtitle">Sign in to manage and print checks</p>
          <button className="btn-google" onClick={handleSignIn}>
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    )
  }

  // Main app
  return (
    <>
      <div className="app-container">
        {/* User bar */}
        <div className="user-bar">
          <span className="user-email">{user.email}</span>
          <button className="btn-signout" onClick={handleSignOut}>Sign Out</button>
        </div>

        {/* Check Form Card */}
        <div className="card">
          <h1 className="card-title">Business Check Printer</h1>

          <h2 className="section-header">Check Details</h2>
          <hr className="section-divider" />

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="checkNumber">Check Number:</label>
              <input
                id="checkNumber"
                className="form-input"
                type="text"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="payee">Pay to the Order of:</label>
              <input
                id="payee"
                className="form-input"
                type="text"
                value={payee}
                onChange={(e) => setPayee(e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="amount">Amount ($):</label>
              <input
                id="amount"
                className={'form-input' + (validateAmount(amount) ? ' error' : '')}
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Amount in Words:</label>
              <input
                className="form-input"
                type="text"
                value={amount ? toWords(amount) : ''}
                readOnly
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="memo">Memo:</label>
              <input
                id="memo"
                className="form-input"
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </div>
          </div>

          <button className="btn-primary" onClick={generateCheck} disabled={generating}>
            {generating ? 'Generating PDF...' : 'Generate Check for Printing'}
          </button>
        </div>

        {/* Check History Card */}
        <div className="card">
          <h2 className="history-title">Check History</h2>
          <ChecksTable checks={checks} setStateFunctions={setStateFunctions} />
        </div>
      </div>
    </>
  );
}

export default App;
